;(function () {
  'use strict'
  if (window.CHICCHOK_CART_FLOW) return

  var mutationQueue = Promise.resolve()
  var pendingWrites = new Set()
  // Telemetry stays serialized and awaited at checkout, without locking the cart UI.
  var pendingPurchaseWrites = new Set()
  var pendingRefreshes = new Set()
  var revision = 0
  var checkoutLocked = false
  var checkoutAttempt = 0
  var failedWrites = 0
  var checkoutFailureBaseline = 0
  var lastWriteError = null
  var compatibilityRefresh = null

  function message(key, fallback) {
    return window.themeVariables && window.themeVariables.strings && window.themeVariables.strings[key] || fallback
  }

  function showError(message) {
    document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: { status: 'error', error: message || 'The cart could not be updated. Please try again.' },
    }))
  }

  function withDeadline(promise, milliseconds) {
    var timer
    return Promise.race([
      promise,
      new Promise(function (_, reject) {
        timer = window.setTimeout(function () {
          reject(new Error('Your cart is still updating. Please try again.'))
        }, milliseconds)
      }),
    ]).finally(function () { window.clearTimeout(timer) })
  }

  async function settleWrites() {
    while (pendingWrites.size) await Promise.allSettled(Array.from(pendingWrites))
  }

  function cartMutationState() {
    document.documentElement.dispatchEvent(new CustomEvent('chicchok:cart-state', {
      bubbles: true,
      detail: { pending: pendingPurchaseWrites.size > 0, revision: revision },
    }))
  }

  function assertPurchasableCart(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      throw new Error(message('cartPurchaseReadError', 'We could not check your cart. Please retry before checkout.'))
    }
    var items = cart.items.filter(function (item) { return Number(item.quantity) > 0 })
    if (!items.length) throw new Error('Your cart is empty. Please add an item before checkout.')
    return cart
  }

  function enqueueMutation(operation) {
    if (checkoutLocked) return Promise.reject(new Error('Checkout is in progress. Please wait before changing your cart.'))
    return enqueueCartMutation(operation, true)
  }

  function enqueueAttributeMaintenance(operation) {
    return enqueueCartMutation(operation, false)
  }

  function enqueueCartMutation(operation, required) {
    var result = mutationQueue.then(function () {
      if (required) revision += 1
      return operation(revision)
    })
    mutationQueue = result.catch(function (error) {
      if (required) {
        failedWrites += 1
        lastWriteError = error
      }
    })
    pendingWrites.add(result)
    if (required) {
      pendingPurchaseWrites.add(result)
      cartMutationState()
    }
    var settled = function () {
      pendingWrites.delete(result)
      if (required) {
        pendingPurchaseWrites.delete(result)
        cartMutationState()
      }
    }
    result.then(settled, settled)
    return result
  }

  function trackExternalMutation(promise) {
    revision += 1
    var result = Promise.resolve(promise).catch(function (error) {
      failedWrites += 1
      lastWriteError = error
      throw error
    })
    pendingWrites.add(result)
    pendingPurchaseWrites.add(result)
    cartMutationState()
    var settled = function () { pendingWrites.delete(result); pendingPurchaseWrites.delete(result); revision += 1; cartMutationState() }
    result.then(settled, settled)
    return result
  }

  function trackRefresh(promise) {
    var result = Promise.resolve(promise)
    pendingRefreshes.add(result)
    result.then(function () { pendingRefreshes.delete(result) }, function () { pendingRefreshes.delete(result) })
    return result
  }

  async function waitForMutations(milliseconds) {
    await withDeadline((async function () {
      while (pendingWrites.size || pendingRefreshes.size) {
        await Promise.allSettled(Array.from(pendingWrites).concat(Array.from(pendingRefreshes)))
      }
    })(), milliseconds || 20000)
    if (checkoutLocked && failedWrites !== checkoutFailureBaseline) {
      throw lastWriteError || new Error('The cart could not be updated. Please try again.')
    }
  }

  function checkoutState(locked) {
    document.documentElement.dispatchEvent(new CustomEvent('chicchok:checkout-state', {
      bubbles: true,
      detail: { locked: locked },
    }))
  }

  function beginCheckout() {
    if (checkoutLocked) return false
    checkoutLocked = true
    checkoutAttempt += 1
    checkoutFailureBaseline = failedWrites
    checkoutState(true)
    return true
  }

  function endCheckout(expectedAttempt) {
    if (!checkoutLocked) return
    if (expectedAttempt !== undefined && expectedAttempt !== checkoutAttempt) return
    checkoutLocked = false
    checkoutState(false)
  }

  function cartUrl() {
    var routes = window.themeVariables && window.themeVariables.routes
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/'
    return new URL(routes && routes.cartUrl || root + 'cart', window.location.origin)
  }

  async function fetchCartSnapshot() {
    var controller = new AbortController()
    var timer = window.setTimeout(function () { controller.abort() }, 10000)
    var url = cartUrl()
    var jsonUrl = new URL(url)
    jsonUrl.pathname = jsonUrl.pathname.replace(/\/$/, '') + '.js'
    var sectionUrl = new URL(url)
    sectionUrl.searchParams.set('section_id', 'mini-cart')
    try {
      var responses = await Promise.all([
        fetch(jsonUrl.toString(), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } }),
        fetch(sectionUrl.toString(), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal }),
      ])
      if (!responses[0].ok || !responses[1].ok) throw new Error('The cart could not be refreshed. Please try again.')
      var values = await Promise.all([responses[0].json(), responses[1].text()])
      var cart = values[0]
      if (!Array.isArray(cart.items) || !Number.isFinite(Number(cart.item_count))) throw new Error('The cart could not be refreshed. Please try again.')
      cart.sections = { 'mini-cart': values[1] }
      return cart
    } finally {
      window.clearTimeout(timer)
    }
  }

  async function refreshCart(options) {
    options = options || {}
    for (var attempt = 0; attempt < 3; attempt += 1) {
      await withDeadline(settleWrites(), 12000)
      var snapshotRevision = ++revision
      var cart = await fetchCartSnapshot()
      if (snapshotRevision !== revision) continue
      var detail = {
        cart: cart,
        cartRevision: snapshotRevision,
        allowCheckoutRender: Boolean(options.allowCheckoutRender),
        replacementDelay: 0,
        openMiniCart: Boolean(options.openMiniCart && !checkoutLocked),
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: detail }))
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: detail }))
      var rendered = detail.renderPromise ? await withDeadline(detail.renderPromise, 12000) : true
      if (snapshotRevision !== revision) continue
      if (options.allowCheckoutRender && rendered === false) throw new Error('The cart has changed. Please try checkout again.')
      return cart
    }
    throw new Error('The cart is changing. Please try again.')
  }

  async function preparePurchaseCheckout(options) {
    options = options || {}
    if (!beginCheckout()) throw new Error(message('cartBusyError', 'Your cart is still updating. Please wait.'))
    var ownedAttempt = checkoutAttempt
    function assertCurrent() {
      if (!checkoutLocked || checkoutAttempt !== ownedAttempt || options.isCurrent && !options.isCurrent()) {
        throw new Error(message('cartBusyError', 'Your cart has changed. Please check your cart before checkout.'))
      }
    }
    try {
      await waitForMutations(20000)
      assertCurrent()
      if (window.CHICCHOK_PROTECTION_GUARD) await window.CHICCHOK_PROTECTION_GUARD.ensureConfirmed()
      assertCurrent()
      var cart = await refreshCart({ allowCheckoutRender: true })
      assertCurrent()
      assertPurchasableCart(cart)
      return cart
    } catch (error) {
      endCheckout(ownedAttempt)
      throw error
    }
  }

  function refreshCompatibilityCart() {
    if (compatibilityRefresh) return compatibilityRefresh
    var task = refreshCart({ openMiniCart: true }).then(function (cart) {
      if (!checkoutLocked) {
        var settings = window.themeVariables && window.themeVariables.settings
        if (settings && settings.cartType === 'page') window.location.assign(cartUrl().toString())
        else {
          var drawer = document.getElementById('mini-cart')
          if (drawer) drawer.open = true
        }
      }
      return cart
    }).catch(function (error) {
      showError(error && error.message)
    })
    compatibilityRefresh = task
    pendingRefreshes.add(task)
    task.finally(function () {
      pendingRefreshes.delete(task)
      compatibilityRefresh = null
    })
    return task
  }

  document.addEventListener('click', function (event) {
    if (event.isTrusted) return
    var path = event.composedPath ? event.composedPath() : [event.target]
    var button = path.find(function (element) { return element && element.tagName === 'BUTTON' && element.type === 'submit' })
    var form = button && button.form
    if (!form || form.getAttribute('is') !== 'product-form') return
    var quantities = Array.from(form.elements).filter(function (element) { return element.name === 'quantity' && !element.disabled })
    var hasZeroRefreshInput = quantities.some(function (element) { return element.type === 'hidden' && Number(element.value) === 0 })
    var hasPositiveQuantity = quantities.some(function (element) { return Number(element.value) > 0 })
    if (!hasZeroRefreshInput || !hasPositiveQuantity) return
    event.preventDefault()
    event.stopImmediatePropagation()
    refreshCompatibilityCart()
  }, true)

  window.CHICCHOK_CART_FLOW = {
    version: '2026-10-21-purchase-pending-v2',
    enqueueMutation: enqueueMutation,
    enqueueAttributeMaintenance: enqueueAttributeMaintenance,
    trackExternalMutation: trackExternalMutation,
    trackRefresh: trackRefresh,
    preparePurchaseCheckout: preparePurchaseCheckout,
    assertPurchasableCart: assertPurchasableCart,
    isCartPending: function () { return pendingWrites.size > 0 },
    isPurchasePending: function () { return pendingPurchaseWrites.size > 0 },
    waitForMutations: waitForMutations,
    beginCheckout: beginCheckout,
    endCheckout: endCheckout,
    isCheckoutLocked: function () { return checkoutLocked },
    getCheckoutAttempt: function () { return checkoutAttempt },
    getRevision: function () { return revision },
    refreshCart: refreshCart,
    showError: showError,
  }
})()

